import { auth, db } from '../lib/firebase';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';

export interface SlipVerificationResult {
  success: boolean;
  data?: {
    amount: number;
    transactionId: string;
    sender: {
      name: string;
      account: string;
      bank: string;
    };
    receiver: {
      name: string;
      account: string;
      bank: string;
    };
    transactionDate: string;
    transactionTime?: string;
    ref1?: string;
    ref2?: string;
  };
  error?: string;
  message?: string;
}

/**
 * Verify slip image using SlipOK API
 * @param slipImageFile - The slip image file to verify
 * @param expectedAmount - Expected payment amount in baht
 * @param receiverAccount - Expected receiver account number (optional)
 * @returns Verification result
 */
export const verifySlip = async (
  slipImageFile: File,
  expectedAmount: number,
  receiverAccount?: string
): Promise<SlipVerificationResult> => {
  try {
    console.log('[SlipOK] Verifying slip...', {
      fileName: slipImageFile.name,
      fileSize: slipImageFile.size,
      expectedAmount,
      receiverAccount
    });

    // Upload slip to R2 via Cloudflare Worker (guest mode - no auth required)
    console.log('[SlipOK] Uploading slip to R2...');
    
    const UPLOAD_API_URL = 'https://edumi-upload-api.edumi-subdomin.workers.dev/slip-upload';
    
    // Upload to R2 (no authentication required for slip upload)
    const uploadResponse = await fetch(UPLOAD_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': slipImageFile.type,
        'X-File-Name': encodeURIComponent(slipImageFile.name),
        'X-File-Size': slipImageFile.size.toString(),
      },
      body: slipImageFile,
    });
    
    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(errorData.error || 'Failed to upload slip to R2');
    }
    
    const uploadResult = await uploadResponse.json();
    const fileUrl = uploadResult.publicUrl;
    
    console.log('[SlipOK] Slip uploaded to R2:', fileUrl);
    
    console.log('[SlipOK] Calling Cloudflare Worker...');

    // Call Cloudflare Worker SlipOK Proxy
    const PROXY_URL = 'https://slipok-proxy.edumi-subdomin.workers.dev';
    
    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileUrl,
        expectedAmount
      }),
    });

    const result = await response.json();
    console.log('[SlipOK] Proxy Response:', result);

    if (!response.ok) {
      console.error('[SlipOK] API Error:', response.status, result);
      return {
        success: false,
        error: result.data?.message || result.message || `SlipOK API Error: ${response.status}`,
        message: result.data?.message || result.message
      };
    }

    // Check if slip verification was successful
    if (!result.success) {
      return {
        success: false,
        error: result.error || result.message || 'ไม่สามารถตรวจสอบสลิปได้',
        message: result.message
      };
    }

    // Parse slip data from proxy response
    const slipData = result.data;
    
    // Validate amount if provided
    if (expectedAmount && slipData.amount) {
      const slipAmount = parseFloat(slipData.amount);
      if (Math.abs(slipAmount - expectedAmount) > 0.01) {
        return {
          success: false,
          error: `จำนวนเงินไม่ตรงกับที่ระบุ (คาดหวัง ฿${expectedAmount}, พบ ฿${slipAmount})`,
          data: {
            amount: slipAmount,
            transactionId: slipData.transactionId || 'N/A',
            sender: slipData.sender || {
              name: 'N/A',
              account: 'N/A',
              bank: 'N/A',
            },
            receiver: slipData.receiver || {
              name: 'N/A',
              account: 'N/A',
              bank: 'N/A',
            },
            transactionDate: slipData.transactionDate || new Date().toISOString(),
            transactionTime: slipData.transactionTime,
            ref1: slipData.ref1,
            ref2: slipData.ref2,
          }
        };
      }
    }

    // Success - slip is valid
    return {
      success: true,
      data: slipData
    };

  } catch (error: any) {
    console.error('[SlipOK] Verification error:', error);
    return {
      success: false,
      error: error.message || 'เกิดข้อผิดพลาดในการตรวจสอบสลิป',
    };
  }
};

/**
 * Record successful payment in Firestore
 * @param userId - User ID (can be null for guest checkout)
 * @param planTier - Plan tier
 * @param amount - Payment amount
 * @param slipData - Slip verification data
 * @param additionalData - Additional data to store
 */
export const recordSlipPayment = async (
  userId: string | null,
  planTier: string,
  amount: number,
  slipData: SlipVerificationResult['data'],
  additionalData?: any
) => {
  try {
    const user = auth.currentUser;
    // Allow guest checkout - user can be null

    // Create payment record
    const paymentData: any = {
      userId: userId || null,
      userEmail: user?.email || null,
      planTier,
      amount,
      paymentMethod: 'slip_promptpay',
      status: userId ? 'completed' : 'pending_registration', // Pending if guest
      slipVerification: slipData,
      transactionId: slipData?.transactionId,
      createdAt: serverTimestamp(),
      verifiedAt: serverTimestamp(),
      isGuestCheckout: !userId,
      ...additionalData
    };

    // Add to paymentLogs collection (consistent with other payment methods)
    const paymentRef = await addDoc(collection(db, 'paymentLogs'), paymentData);
    console.log('[SlipOK] Payment recorded:', paymentRef.id, 'isGuest:', !userId);

    return paymentRef.id;
  } catch (error) {
    console.error('[SlipOK] Error recording payment:', error);
    throw error;
  }
};
